import React from 'react';
import { useQuery } from '@apollo/client/react';
import GET_DIDS_FOR_ACCOUNT_Q from '../gql/getDidsForAccount.gql';

import { Table } from '@repo/ui/Table';
import { PAGE_SCHEMA, Pagination, decodePaging } from '@repo/ui/Pagination';
import type { SelectInputOptions } from '@repo/types/Types';
import { z } from 'zod';
import { getNonEmpty } from '@repo/util/nonEmpty';
import { useZodQuery } from '@repo/hooks/useZodQuery';
import { coerce } from '@repo/util/coerceForTable';
import { toQueryList } from '@repo/util/toQueryList';
import { combineDateRange } from '@repo/util/combineDateRange';
import { coerceValue } from '@repo/util/coerceValueForQuery';
import { Form } from '@repo/ui/Form';
import { InfoPopover } from '@repo/ui/Popover';
import DidsTableFilter from './DidsTableFilter';

import { POLICIES } from '@/lib/Authorization';
import { useUser } from '@repo/hooks/useUser';
import { Link } from 'react-router';

const TYPES: SelectInputOptions = [
  { label: '10DLC', value: 'TENDLC' },
  { label: 'Toll Free', value: 'TOLLFREE' },
  { label: 'Short Code', value: 'SHORTCODE' },
] as const;

const DIDS_QUERY_SCHEMA = PAGE_SCHEMA.extend({
  did_id: z.string().ulid().or(z.literal('')).default(''),
  number: z.string().default(''),
  type: z
    .enum(getNonEmpty(TYPES.map((dir) => dir.value)))
    .default('')
    .or(z.literal(''))
    .default(''),

  start: z.coerce.date().optional(),
  end: z.coerce.date().optional(),
});

const PAGE_SIZE = 10;

export interface DidsTableProps {
  accountId?: string;
  cspCampaignId?: string;
}

const DidsTable = ({ accountId, cspCampaignId }: DidsTableProps) => {
  const user = useUser();

  const filter = useZodQuery({
    schema: DIDS_QUERY_SCHEMA,
    ignoreList: ['page'],
    coerce,
  });

  const { page, start, end, did_id, ...queryFields } = filter.queryValues;
  const queryList = [
    ...toQueryList(
      {
        ...queryFields,
        inserted_at: combineDateRange(start, end),
        csp_campaign_id: cspCampaignId,
        id: did_id,
      },
      coerceValue,
      ['number']
    ),
  ];

  const { loading, error, data } = useQuery(GET_DIDS_FOR_ACCOUNT_Q, {
    variables: {
      ...decodePaging(page, PAGE_SIZE),
      queryList,
      id: accountId,
    },
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    console.error(error);
  }

  const { edges: didNodes, pageInfo } = data?.didsForAccount ?? {};

  return (
    <div>
      <Form {...filter} variant="horizontal" className={'mb-6'}>
        <DidsTableFilter
          TYPES={TYPES}
          queryList={queryList}
          hasContent={loading || (didNodes?.length ?? 0) > 0}
        />
      </Form>
      <Table
        loading={loading}
        pageSize={PAGE_SIZE}
        data={didNodes?.map((did) => did.node) ?? []}
        dataType="numbers"
        columns={[
          {
            title: 'Number',
            Cell({ entry: { number, id, isByon } }) {
              return (
                <span className="flex gap-2 items-center">
                  {POLICIES['dids:show'](user) ? (
                    <Link to={`/a/${accountId}/numbers/${id}`} className="underline">
                      {number}
                    </Link>
                  ) : (
                    <span>{number}</span>
                  )}
                  {isByon && (
                    <InfoPopover btnText="BYON" btnIcon="info" size="sm">
                      <span className="break-normal text-pretty">
                        BYON (Bring Your Own Number): A landline or VoIP number you own with your
                        current carrier that you have connected to Swift to enable SMS messaging on
                        your account.
                      </span>
                    </InfoPopover>
                  )}
                </span>
              );
            },
          },
          {
            title: 'Type',
            Cell({ entry: { type } }) {
              if (type === 'TENDLC') return <p>10DLC</p>;
              return <p>{type}</p>;
            },
          },
          {
            title: 'Capabilities',
            Cell({ entry: { smsEnabled, mmsEnabled } }) {
              return (
                <div className="flex gap-10 justify-between items-center w-min">
                  <span
                    className={`material-symbols-outlined ${
                      smsEnabled
                        ? 'dark:text-blue-100 text-grey-1000'
                        : 'dark:text-grey-600 text-grey-500'
                    }`}
                  >
                    sms
                  </span>
                  <span
                    className={`material-symbols-outlined ${
                      mmsEnabled
                        ? 'dark:text-blue-100 text-grey-1000'
                        : 'dark:text-grey-600 text-grey-500'
                    }`}
                  >
                    mms
                  </span>
                </div>
              );
            },
          },
          {
            title: 'Auto Responder',
            Cell({ entry: { autoResponse } }) {
              return <span>{autoResponse ? 'Active' : 'Inactive'}</span>;
            },
          },
        ]}
        error={!!error}
      />
      {!error ? <Pagination {...{ pageInfo, filter }} /> : null}
    </div>
  );
};

export default DidsTable;
